import { createApp, reactive, h } from 'vue'
import ResourceContextMenu from '../components/ResourceContextMenu.vue'
import PermissionModal from '../components/PermissionModal.vue'
import ResourceDetailsPanel from '../components/ResourceDetailsPanel.vue'

const bool = (value) => value === 'true'

const parseResource = (node) => ({
  resourceType: node.dataset.aclResourceType,
  resourceId: Number(node.dataset.aclResourceId),
  parentFolderId: node.dataset.aclParentFolderId
    ? Number(node.dataset.aclParentFolderId)
    : null,
  canView: bool(node.dataset.aclCanView),
  canEdit: bool(node.dataset.aclCanEdit),
  canSend: bool(node.dataset.aclCanSend),
  canSign: bool(node.dataset.aclCanSign),
  canAdmin: bool(node.dataset.aclCanAdmin),
  urls: {
    view: node.dataset.aclViewUrl || null,
    edit: node.dataset.aclEditUrl || null,
    send: node.dataset.aclSendUrl || null,
    sign: node.dataset.aclSignUrl || null
  }
})

export default class extends HTMLElement {
  connectedCallback () {
    this.appElem = document.createElement('div')
    document.body.appendChild(this.appElem)

    const i18n = JSON.parse(this.dataset.i18n || '{}')

    const state = reactive({
      menu: null,
      modalResource: null,
      detailsResource: null,
      detailsRefreshKey: 0
    })

    this.onContextMenu = (event) => {
      const resourceNode = event.target.closest('[data-acl-resource="true"]')
      if (!resourceNode) return

      const resource = parseResource(resourceNode)
      if (!resource.canView) return

      event.preventDefault()
      state.menu = {
        resource,
        position: {
          x: event.clientX,
          y: event.clientY
        }
      }
    }

    this.onManageAccessClick = (event) => {
      const trigger = event.target.closest('[data-acl-manage-access="true"]')
      if (!trigger) return

      event.preventDefault()
      event.stopPropagation()

      const resourceNode = trigger.closest('[data-acl-resource="true"]')
      if (!resourceNode) return

      const resource = parseResource(resourceNode)
      if (!resource.canAdmin) return

      state.modalResource = {
        resourceType: resource.resourceType,
        resourceId: resource.resourceId,
        parentFolderId: resource.parentFolderId
      }
    }

    this.onDetailsOpenClick = (event) => {
      const trigger = event.target.closest('[data-resource-details-open="true"]')
      if (!trigger) return

      event.preventDefault()
      event.stopPropagation()

      const resourceNode = trigger.closest('[data-acl-resource="true"]')
      if (!resourceNode) return

      const resource = parseResource(resourceNode)
      if (!resource.canView) return

      state.detailsResource = {
        resourceType: resource.resourceType,
        resourceId: resource.resourceId,
        parentFolderId: resource.parentFolderId,
        canAdmin: resource.canAdmin
      }
    }

    this.onKeyDown = (event) => {
      if (event.key !== 'Escape') return

      if (state.modalResource) {
        state.modalResource = null
        if (state.detailsResource) {
          state.detailsRefreshKey += 1
        }
      } else if (state.detailsResource) {
        state.detailsResource = null
      } else {
        state.menu = null
      }
    }

    document.addEventListener('contextmenu', this.onContextMenu)
    document.addEventListener('click', this.onManageAccessClick)
    document.addEventListener('click', this.onDetailsOpenClick)
    document.addEventListener('keydown', this.onKeyDown)

    const root = {
      components: { ResourceContextMenu, PermissionModal, ResourceDetailsPanel },
      data: () => ({ state, i18n }),
      methods: {
        closeMenu () {
          this.state.menu = null
        },
        closeDetails () {
          this.state.detailsResource = null
        },
        onAction (kind) {
          const url = this.state.menu?.resource?.urls?.[kind]
          this.closeMenu()

          if (url) {
            window.location.href = url
          }
        },
        onManageAccess (resource) {
          const target = resource || this.state.menu?.resource

          if (!target) return

          this.state.modalResource = {
            resourceType: target.resourceType,
            resourceId: target.resourceId,
            parentFolderId: target.parentFolderId ?? null
          }

          this.closeMenu()
        },
        onModalClose () {
          this.state.modalResource = null

          if (this.state.detailsResource) {
            this.state.detailsRefreshKey += 1
          }
        }
      },
      render () {
        return h('div', [
          this.state.menu
            ? h(ResourceContextMenu, {
                resource: this.state.menu.resource,
                position: this.state.menu.position,
                onAction: this.onAction,
                onClose: this.closeMenu,
                onManageAccess: () => this.onManageAccess()
              })
            : null,
          this.state.detailsResource
            ? h(ResourceDetailsPanel, {
                resource: this.state.detailsResource,
                refreshKey: this.state.detailsRefreshKey,
                onClose: this.closeDetails,
                onManageAccess: () => this.onManageAccess(this.state.detailsResource)
              })
            : null,
          this.state.modalResource
            ? h(PermissionModal, {
                resource: this.state.modalResource,
                i18n: this.i18n,
                onClose: this.onModalClose
              })
            : null
        ])
      }
    }

    this.app = createApp(root)
    this.app.mount(this.appElem)
  }

  disconnectedCallback () {
    document.removeEventListener('contextmenu', this.onContextMenu)
    document.removeEventListener('click', this.onManageAccessClick)
    document.removeEventListener('click', this.onDetailsOpenClick)
    document.removeEventListener('keydown', this.onKeyDown)

    this.app?.unmount()
    this.appElem?.remove()
  }
}
